import type {
    CrmFieldType,
    ExcludeAccountFields,
    SalesforceMappingField,
} from "app/pages/sales-intelligence/common-components/export-table/types";
import { createCancelable } from "app/pages/workspace/sales/helpers";
import type { IFetchService } from "app/services/fetchService";
import type { SalesforcePreDefinedType } from "../types/shared";
import { ExportSettingsSourceType } from "../types/shared";
import type { SalesforcePredefinedApiServiceType } from "./salesforce/predefined";
import { salesforcePredefinedApiService } from "./salesforce/predefined";
import type { SalesforceStatusServiceType } from "./salesforce/status";
import { salesforceStatusService } from "./salesforce/status";
import type { SalesforceToggleServiceType } from "./salesforce/toggles";
import { salesforceTogglesService } from "./salesforce/toggles";

export type SalesforceServiceType = {
    getSfMappingFields(): Promise<SalesforceMappingField[]>;
    getSfFields(
        columnName: string,
        type: SalesforcePreDefinedType,
        cacheToken: string,
        sourceType?: ExportSettingsSourceType,
    ): Promise<CrmFieldType[]>;
    getSfColumns(type: SalesforcePreDefinedType, cacheToken: string, signal: AbortSignal): Promise<CrmFieldType[]>;
    putSfStatusField(externalField?: string): Promise<{ value: boolean }>;
    getSfExcludeAccountFields(): Promise<ExcludeAccountFields>;
    putSfFilterFields(fields: string[]): Promise<{ value: boolean }>;
};

export const salesforceService = (
    fetchService: IFetchService,
): SalesforceServiceType &
    SalesforceToggleServiceType &
    SalesforceStatusServiceType &
    SalesforcePredefinedApiServiceType => {
    return {
        getSfFields: createCancelable(
            (
                signal: AbortSignal,
                columnName: string,
                type: SalesforcePreDefinedType,
                cacheToken: string,
                sourceType: ExportSettingsSourceType,
            ): Promise<CrmFieldType[]> => {
                const params = new URLSearchParams();

                params.set(
                    "column",
                    sourceType === ExportSettingsSourceType.Technologies ? "techCategory" : columnName,
                );
                if (sourceType) {params.set("sourceType", sourceType.toString());}
                params.set("cacheToken", cacheToken);
                const url = `sales-api/salesforce/${type}-applicable-columns?${params.toString()}`;

                return fetchService.get(url, undefined, {
                    cancellation: signal,
                });
            },
        ),
        async getSfColumns(
            type: SalesforcePreDefinedType,
            cacheToken: string,
            cancellation: AbortSignal,
        ): Promise<CrmFieldType[]> {
            const response = await fetchService.get<CrmFieldType[]>(
                `sales-api/salesforce/${type}-columns?cacheToken=${cacheToken}`,
                undefined,
                { cancellation },
            );

            return response || [];
        },
        getSfExcludeAccountFields: (): Promise<ExcludeAccountFields> => {
            return fetchService.get("sales-api/salesforce/account-text-columns");
        },
        getSfMappingFields: createCancelable((signal: AbortSignal): Promise<SalesforceMappingField[]> => {
            return fetchService.get("sales-api/salesforce/mapping-field?type=AccountImport", undefined, {
                cancellation: signal,
            });
        }),

        putSfStatusField: createCancelable(
            (signal: AbortSignal, externalField: string): Promise<{ value: boolean }> => {
                return fetchService.put(
                    `sales-api/salesforce/mapping-field/import/type?salesforceField=${externalField}`,
                    undefined,
                    {
                        cancellation: signal,
                    },
                );
            },
        ),

        putSfFilterFields: createCancelable((signal: AbortSignal, fields: string[]): Promise<{ value: boolean }> => {
            return fetchService.put("sales-api/salesforce/mapping-field/import/lists", fields, {
                cancellation: signal,
            });
        }),
        ...salesforceTogglesService(fetchService),
        ...salesforceStatusService(fetchService),
        ...salesforcePredefinedApiService(fetchService),
    };
};
